import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { ChartContainer, CHART_COLORS } from './shared';
import type { RespondentType } from './shared';
import Navbar from '../layout/Navbar';
import './AdminDashboard.css';

interface StatsOverview {
  totalResponses: number;
  totalUsers: number;
  completionRate: number;
  surveysActive: number;
}

interface TimelineData {
  date: string;
  surveyId: string;
  responses: number;
}

interface ResponseData {
  name: string;
  totalUsers: number;
  completedUsers: number;
  completionRate: number;
}

interface Alert {
  id: string;
  type: 'anomaly' | 'threshold' | 'trend';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  metric: string;
  currentValue: number;
  relatedData?: Record<string, any>;
}

const AdminDashboard: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineData[]>([]);
  const [responsesByType, setResponsesByType] = useState<ResponseData[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [respondentType, setRespondentType] = useState<RespondentType>('student');

  const isOverviewPage = location.pathname === '/admin';

  useEffect(() => {
    const abortController = new AbortController();

    const fetchAllData = async () => {
      if (!token) return;

      setIsLoading(true);
      setError(null);

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const signal = abortController.signal;

        const roleParam = `role=${respondentType}`;

        // Fetch all data in parallel
        const [statsRes, timelineRes, responsesRes, alertsRes] = await Promise.all([
          fetch(`/api/admin/stats/overview?${roleParam}`, { headers, signal }),
          fetch(`/api/admin/stats/timeline?period=day&${roleParam}`, { headers, signal }),
          fetch(`/api/admin/stats/responses?groupBy=survey_type&${roleParam}`, { headers, signal }),
          fetch(`/api/admin/stats/alerts?${roleParam}`, { headers, signal }),
        ]);

        // Check if request was aborted
        if (signal.aborted) return;

        if (!statsRes.ok) throw new Error('Failed to fetch stats');

        const statsData = await statsRes.json();
        setStats(statsData);

        if (timelineRes.ok) {
          const timelineJson = await timelineRes.json();
          // Aggregate by date (combine all survey types)
          const aggregated: Record<string, number> = {};
          for (const item of timelineJson.data) {
            const dateStr = new Date(item.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
            aggregated[dateStr] = (aggregated[dateStr] || 0) + item.responses;
          }
          setTimelineData(
            Object.entries(aggregated).map(([date, responses]) => ({
              date,
              surveyId: 'all',
              responses,
            }))
          );
        }

        if (responsesRes.ok) {
          const responsesJson = await responsesRes.json();
          setResponsesByType(responsesJson.data);
        }

        if (alertsRes.ok) {
          const alertsJson = await alertsRes.json();
          setAlerts(alertsJson.alerts.slice(0, 5)); // Show top 5 alerts
        }
      } catch (err: any) {
        // Ignore abort errors
        if (err.name === 'AbortError') return;
        setError(err.message);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (isOverviewPage) {
      fetchAllData();
    }

    return () => {
      abortController.abort();
    };
  }, [token, isOverviewPage, respondentType]);

  const navLinks = [
    { path: '/admin', label: 'Overview', exact: true },
    { path: '/admin/responses', label: 'Response Rates' },
    { path: '/admin/questions', label: 'Question Breakdowns' },
    { path: '/admin/comparison', label: 'Version Comparison' },
    { path: '/admin/trends', label: 'Trends' },
    { path: '/admin/export', label: 'Export' },
  ];

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Prepare pie chart data
  const pieData = responsesByType
    .filter((r) => r.completedUsers > 0)
    .map((r) => ({
      name: r.name
        .replace('student-', '')
        .replace('staff-', '')
        .replace(/-/g, ' ')
        .toUpperCase(),
      value: r.completedUsers,
    }));

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '#ff3b30';
      case 'warning':
        return '#ff9500';
      default:
        return '#007aff';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '!';
      case 'warning':
        return '!';
      default:
        return 'i';
    }
  };

  return (
    <>
      <Navbar />
      <div className="admin-dashboard">
        <div className="admin-header">
          <div className="admin-header-content">
            <div className="admin-header-row">
              <div>
                <h1>Admin Dashboard</h1>
                <p className="admin-subtitle">View and analyze survey responses across the district</p>
              </div>
              <div className="respondent-type-selector">
                {(['student', 'staff', 'parent'] as RespondentType[]).map((type) => (
                  <button
                    key={type}
                    className={`respondent-type-btn ${respondentType === type ? 'active' : ''}`}
                    onClick={() => setRespondentType(type)}
                    disabled={type === 'parent'}
                  >
                    {type === 'student' ? 'Students' : type === 'staff' ? 'Staff' : 'Parents'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <nav className="admin-nav">
          <div className="admin-nav-content">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`admin-nav-link ${isActive(link.path, link.exact) ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="admin-content">
          {isOverviewPage ? (
            <div className="overview-page">
              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner" />
                  <p>Loading statistics...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>Error loading statistics: {error}</p>
                </div>
              ) : stats ? (
                <>
                  {/* KPI Cards */}
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-value">{stats.totalResponses.toLocaleString()}</div>
                      <div className="stat-label">Total Responses</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
                      <div className="stat-label">Registered Users</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">
                        {stats.completionRate}%
                      </div>
                      <div className="stat-label">Completion Rate</div>
                      <div className="stat-progress">
                        <div
                          className="stat-progress-fill"
                          style={{ width: `${stats.completionRate}%` }}
                        />
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{stats.surveysActive}</div>
                      <div className="stat-label">Active Surveys</div>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="charts-row">
                    {/* Timeline Chart */}
                    <ChartContainer
                      title="Response Timeline"
                      subtitle="Daily responses over time"
                      className="chart-wide"
                      isEmpty={timelineData.length === 0}
                      emptyMessage="No response data available"
                    >
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={timelineData}>
                          <defs>
                            <linearGradient id="colorResponses" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                              <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: '#6d6d70' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e5ea' }}
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: '#6d6d70' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e5ea' }}
                          />
                          <Tooltip
                            contentStyle={{
                              background: '#fff',
                              border: '1px solid #e5e5ea',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="responses"
                            stroke={CHART_COLORS.primary}
                            strokeWidth={2}
                            fill="url(#colorResponses)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartContainer>

                    {/* Pie Chart */}
                    <ChartContainer
                      title="Responses by Survey Type"
                      subtitle="Distribution of completed surveys"
                      className="chart-narrow"
                      isEmpty={pieData.length === 0}
                      emptyMessage="No completion data"
                    >
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {pieData.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={CHART_COLORS.series[index % CHART_COLORS.series.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [Number(value).toLocaleString(), 'Responses']}
                            contentStyle={{
                              background: '#fff',
                              border: '1px solid #e5e5ea',
                              borderRadius: '8px',
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value) => (
                              <span style={{ color: '#1d1d1f', fontSize: '0.8rem' }}>{value}</span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </div>

                  {/* Alerts Section */}
                  {alerts.length > 0 && (
                    <div className="alerts-section">
                      <h2>Alerts & Insights</h2>
                      <div className="alerts-list">
                        {alerts.map((alert) => (
                          <div
                            key={alert.id}
                            className={`alert-card alert-${alert.severity}`}
                          >
                            <div
                              className="alert-icon"
                              style={{ backgroundColor: getSeverityColor(alert.severity) }}
                            >
                              {getSeverityIcon(alert.severity)}
                            </div>
                            <div className="alert-content">
                              <div className="alert-title">{alert.title}</div>
                              <div className="alert-message">{alert.message}</div>
                            </div>
                            <div className="alert-type">{alert.type}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="quick-actions">
                    <h2>Quick Actions</h2>
                    <div className="actions-grid">
                      <Link to="/admin/responses" className="action-card">
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M16 8V16M12 11V16M8 14V16M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="action-text">
                          <span className="action-title">View Response Rates</span>
                          <span className="action-desc">See completion rates by school and grade</span>
                        </div>
                      </Link>
                      <Link to="/admin/comparison" className="action-card">
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M9 17V7M9 7L5 11M9 7L13 11M15 7V17M15 17L19 13M15 17L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="action-text">
                          <span className="action-title">Compare Versions</span>
                          <span className="action-desc">Compare survey results over time</span>
                        </div>
                      </Link>
                      <Link to="/admin/questions" className="action-card">
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M8.5 8.5C8.5 6.01472 10.5147 4 13 4C15.4853 4 17.5 6.01472 17.5 8.5C17.5 10.3612 16.3512 11.9464 14.7 12.6192V14M14.7 17.5V17.51M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="action-text">
                          <span className="action-title">Question Analysis</span>
                          <span className="action-desc">View per-question answer breakdowns</span>
                        </div>
                      </Link>
                      <Link to="/admin/export" className="action-card">
                        <div className="action-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="action-text">
                          <span className="action-title">Export Data</span>
                          <span className="action-desc">Download survey data as CSV or JSON</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <Outlet context={{ respondentType }} />
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
